<template>
  <div class="mx-auto my-12 flex w-full items-end justify-center">
    <div
      class="flex w-full items-end justify-center gap-12"
      data-test="promo-code-container"
    >
      <RevInputText
        id="copy-clipboard-input"
        class="w-[370px]"
        :class="[styles.clipboardInput, inputClass]"
        data-test="copy-clipboard-input"
        :disabled="true"
        :hasClearButton="false"
        :label="inputLabel || i18n(translations.inputCopy)"
        :model-value="promotionCode"
      />
      <RevButton
        data-test="copy-to-clipboard"
        variant="secondary"
        @click="handleClick"
      >
        {{ i18n(translations.buttonCopy) }}
      </RevButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useCssModule } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevInputText } from '@ds/components/InputText'

import { LOG_ERROR_MESSAGE } from '../ReferAFriend.constants'
import translations from '../ReferAFriend.translations'
import { REFER_FRIEND_TRACKING } from '../tracking.constants'

export type CopyClipBoardProps = {
  promotionCode: string
  inputLabel?: string
  inputClass?: string
  trackingData?: Record<string, string>
}

const { trackClick } = useTracking()
const props = defineProps<CopyClipBoardProps>()

const i18n = useI18n()
const { openSuccessToast } = useTheToast()
const logger = useLogger()

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(props.promotionCode)

    return true
  } catch (err) {
    const error = err as Error

    logger.error(LOG_ERROR_MESSAGE.COPY_TO_CLIPBOARD, {
      error,
      feature: 'refer-a-friend',
      refer_friend_action: 'copy_code',
      code_length: props.promotionCode.length,
      owners: ['bot-squad-lifecycle-front'],
    })

    return false
  }
}

const handleClick = async () => {
  if (await copyToClipboard()) {
    openSuccessToast({
      title: i18n(translations.toastCopyTitle),
      content: i18n(translations.toastCopyText),
      name: 'TOAST_COPY_CLIPBOARD',
    })

    trackClick({
      name: 'copy_code',
      zone: REFER_FRIEND_TRACKING.ZONE,
      value: props.promotionCode,
      ...(props.trackingData || {}),
    })
  }
}

const styles = useCssModule()
</script>

<style module>
.clipboardInput {
  input {
    @apply !bg-static-default-low;
  }
}
</style>
